import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Progress, Tag, Space, Button, Typography, Alert, Spin, message, Tabs, Badge, Tooltip, InputNumber, Popconfirm, Table, theme, Row, Col, Dropdown, Modal } from 'antd';
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  RotateCw,
  PauseCircle,
  PlayCircle,
  StopCircle,
  Zap,
  ChevronUp,
  ChevronDown,
  Pause,
  Repeat,
  Timer,
  AlertTriangle,
  Webhook,
} from 'lucide-react';
import { api } from '../../services';
import { formatWarehouseLabel } from '../../utils/helpers';
import { pickSafeRussianBackendMessage } from '../../utils/safeRussianBackendMessage';
import EnhancedProgressBar from './EnhancedProgressBar';
import { useBreakpointScreens } from '../../hooks/useBreakpointScreens';
import {
  resolveProcessCounts,
  isProcessVisiblyStuck,
  resolveProcessHeartbeatAt,
  resolveProcessKindTag,
  resolveProcessEtaMinutes,
  formatEtaMinutes,
  resolveProcessRetry,
  stripUserVisibleUuid,
  sanitizeUserVisibleOpsText,
  classifyProcessMonitorFetchError,
  buildProcessCardActionKeys,
  resolveProcessSlotUsage,
  processMonitorActionNeedsConfirm,
  resolveProcessMonitorConfirm
} from '../../utils/processMonitorDisplay';

const { Text } = Typography;

const ProcessProgressMonitor = ({ refreshInterval = 5000, embedded = false }) => {
  const { token } = theme.useToken();
  const screens = useBreakpointScreens();
  const isMobile = !screens.md;
  const [processes, setProcesses] = useState([]);
  const [history, setHistory] = useState([]);
  const [usage, setUsage] = useState({});
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [warehouseLabelById, setWarehouseLabelById] = useState({});
  const [activeTab, setActiveTab] = useState('processes');
  const [historyPagination, setHistoryPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const processesRef = useRef([]);
  const historyRef = useRef([]);
  const historyPaginationRef = useRef({ current: 1, pageSize: 20, total: 0 });
  const loadActiveProcessesRef = useRef(null);
  const loadHistoryRef = useRef(null);
  
  // 🚀 НОВОЕ: Отслеживание последнего обновления и ручное обновление
  const [lastUpdate, setLastUpdate] = useState(null);
  const [, setIsManualRefresh] = useState(false);
  const lastFetchRef = useRef(0);
  
  // 🆕 Лимиты очереди по тарифу
  const [queueLimits, setQueueLimits] = useState(null);

  // Короткий справочник складов, чтобы не показывать "id" как единственную подпись
  useEffect(() => {
    (async () => {
      try {
        const resp = await api.warehouses.getWarehouses();
        const list = Array.isArray(resp?.warehouses) ? resp.warehouses : (Array.isArray(resp) ? resp : []);
        const map = {};
        for (const w of list) {
          if (!w) continue;
          const label = formatWarehouseLabel(w, { includeSupplier: false });
          if (w.id != null) map[String(w.id)] = label;
          if (w.external_warehouse_id != null) map[String(w.external_warehouse_id)] = label;
          if (w.warehouse_id != null) map[String(w.warehouse_id)] = label;
        }
        setWarehouseLabelById(map);
      } catch (_) {
        setWarehouseLabelById({});
      }
    })();
  }, []);

  // 🚀 ОПТИМИЗАЦИЯ: Загрузка процессов с защитой от частых запросов
  const loadActiveProcesses = useCallback(async (forceRefresh = false) => {
    // Защита от слишком частых запросов (минимум 2 секунды между запросами)
    const now = Date.now();
    if (!forceRefresh && now - lastFetchRef.current < 2000) {
      return;
    }
    lastFetchRef.current = now;

    try {
      if (forceRefresh) {
        setIsManualRefresh(true);
      }
      setLoading(true);
      setError(null);

      const [processesRes, usageRes, limitsRes] = await Promise.all([
        api.userResources.getActiveProcesses(),
        api.userResources.getResourceUsage(),
        api.userResources.getQueueLimits().catch(() => ({ success: false })) // Не критично если не загрузится
      ]);

      if (processesRes.success) {
        setProcesses(processesRes.data);
      }

      if (usageRes.success) {
        const d = usageRes.data || {};
        setUsage({
          ...d,
          backpressure: Boolean(d.backpressure || usageRes.backpressure)
        });
      }
      
      if (limitsRes.success) {
        setQueueLimits(limitsRes.data);
      }
      
      setLastUpdate(new Date());
    } catch (err) {
      setError(classifyProcessMonitorFetchError(err));
    } finally {
      setLoading(false);
      setIsManualRefresh(false);
    }
  }, []);

  // Остановка процесса
  const stopProcess = async (processId) => {
    try {
      await api.userResources.stopProcess(processId);
      message.success('Процесс остановлен');
      // Перезагружаем процессы
      await loadActiveProcesses();
    } catch (err) {
      message.error(
        pickSafeRussianBackendMessage(err.response?.data?.error, 'Не удалось остановить процесс. Попробуйте снова.')
      );
    }
  };

  // Пауза процесса
  const pauseProcess = async (processId) => {
    try {
      await api.userResources.pauseProcess(processId);
      message.success('Процесс поставлен на паузу');
      await loadActiveProcesses();
    } catch (err) {
      message.error(
        pickSafeRussianBackendMessage(err.response?.data?.error, 'Не удалось поставить процесс на паузу.')
      );
    }
  };

  // Продолжить процесс
  const resumeProcess = async (processId) => {
    try {
      await api.userResources.resumeProcess(processId);
      message.success('Процесс продолжен');
      await loadActiveProcesses();
    } catch (err) {
      message.error(
        pickSafeRussianBackendMessage(err.response?.data?.error, 'Не удалось продолжить процесс.')
      );
    }
  };

  // Принудительный запуск процесса из очереди
  const forceStartProcess = async (processId) => {
    try {
      const result = await api.userResources.forceStartProcess(processId);
      const text = pickSafeRussianBackendMessage(result.message, 'Процесс переочереден и будет запущен');
      if (text.includes('уже отправлен') || text.includes('подождите')) {
        message.info(text);
      } else {
        message.success(text);
      }
      await loadActiveProcesses();
    } catch (err) {
      message.error(
        pickSafeRussianBackendMessage(err.response?.data?.error, 'Не удалось принудительно запустить процесс из очереди.')
      );
    }
  };

  // Остановить все
  const stopAll = async () => {
    try {
      await api.userResources.stopAllProcesses();
      message.success('Все активные процессы остановлены');
      await loadActiveProcesses();
    } catch (err) {
      message.error(
        pickSafeRussianBackendMessage(err.response?.data?.error, 'Не удалось остановить все процессы.')
      );
    }
  };

  // Принудительная остановка всех процессов
  const forceStopAll = async () => {
    try {
      const result = await api.userResources.forceStopAllProcesses();
      message.success(pickSafeRussianBackendMessage(result.message, 'Все процессы принудительно остановлены'));
      await loadActiveProcesses();
    } catch (err) {
      message.error(
        pickSafeRussianBackendMessage(err.response?.data?.error, 'Не удалось принудительно остановить процессы.')
      );
    }
  };

  // Пауза всех процессов
  const pauseAll = async () => {
    try {
      const result = await api.userResources.pauseAllProcesses();
      message.success(pickSafeRussianBackendMessage(result.message, 'Все процессы поставлены на паузу'));
      await loadActiveProcesses();
    } catch (err) {
      message.error(
        pickSafeRussianBackendMessage(err.response?.data?.error, 'Не удалось поставить все процессы на паузу.')
      );
    }
  };

  // Продолжить все процессы
  const resumeAll = async () => {
    try {
      const result = await api.userResources.resumeAllProcesses();
      message.success(pickSafeRussianBackendMessage(result.message, 'Все процессы продолжены'));
      await loadActiveProcesses();
    } catch (err) {
      message.error(
        pickSafeRussianBackendMessage(err.response?.data?.error, 'Не удалось продолжить все процессы.')
      );
    }
  };

  // Изменить приоритет процесса
  const updatePriority = async (processId, priority, queuePosition = null) => {
    try {
      await api.userResources.updateProcessPriority(processId, priority, queuePosition);
      message.success('Приоритет процесса обновлен');
      await loadActiveProcesses();
    } catch (err) {
      message.error(
        pickSafeRussianBackendMessage(err.response?.data?.error, 'Не удалось обновить приоритет процесса.')
      );
    }
  };

  // Загрузить историю процессов
  const loadHistory = async (page = 1, pageSize = 20) => {
    try {
      setHistoryLoading(true);
      const result = await api.userResources.getProcessHistory({
        limit: pageSize,
        offset: (page - 1) * pageSize
      });

      if (result.success) {
        setHistory(result.data || []);
        if (result.pagination) {
          setHistoryPagination({
            current: page,
            pageSize,
            total: result.pagination.total || 0
          });
        }
      }
    } catch (err) {
      message.error(
        classifyProcessMonitorFetchError(err, 'Не удалось загрузить историю процессов.')
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    processesRef.current = processes;
  }, [processes]);

  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    historyPaginationRef.current = historyPagination;
  }, [historyPagination]);

  useEffect(() => {
    loadActiveProcessesRef.current = loadActiveProcesses;
  }, [loadActiveProcesses]);

  useEffect(() => {
    loadHistoryRef.current = loadHistory;
  });

  // 🚀 ОПТИМИЗАЦИЯ: Умное обновление с минимальной нагрузкой
  useEffect(() => {
    let interval;
    let isActive = true;

    const tick = async () => {
      // Не обновляем если вкладка скрыта или компонент неактивен
      if (document.hidden || !isActive) return;

      // Если нет активных процессов, обновляем реже (каждые 30 сек)
      const hasActiveProcesses = processesRef.current.some(p => p.status === 'running' || p.status === 'pending');
      const actualInterval = hasActiveProcesses ? Math.max(5000, refreshInterval) : 30000;

      await loadActiveProcessesRef.current?.();

      // Динамически изменяем интервал
      if (interval) {
        clearInterval(interval);
        interval = setInterval(tick, actualInterval);
      }
    };

    // Первоначальная загрузка
    tick();

    // Устанавливаем интервал
    const hasActiveProcesses = processesRef.current.some(p => p.status === 'running' || p.status === 'pending');
    const initialInterval = hasActiveProcesses ? Math.max(5000, refreshInterval) : 30000;
    interval = setInterval(tick, initialInterval);

    // Обработчик изменения видимости
    const onVisibility = () => {
      if (!document.hidden && isActive) {
        tick();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      isActive = false;
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshInterval, processes.length]); // Добавляем зависимость от количества процессов

  // Получение цвета статуса
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'running': return 'blue';
      case 'paused': return 'gold';
      case 'completed': return 'green';
      case 'failed': return 'red';
      case 'cancelled': return 'gray';
      case 'cancelling': return 'volcano'; // Новый статус для процессов в процессе отмены
      default: return 'default';
    }
  };

  // Получение иконки статуса
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock size={14} aria-hidden />;
      case 'running': return <RefreshCw size={14} className="mb-animate-spin" aria-hidden />;
      case 'paused': return <Clock size={14} aria-hidden />;
      case 'completed': return <CheckCircle size={14} aria-hidden />;
      case 'failed': return <XCircle size={14} aria-hidden />;
      case 'cancelled': return <XCircle size={14} aria-hidden />;
      default: return <Clock size={14} aria-hidden />;
    }
  };

  // Получение текста статуса
  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'В очереди';
      case 'running': return 'Выполняется';
      case 'paused': return 'На паузе';
      case 'completed': return 'Завершено';
      case 'failed': return 'Ошибка';
      case 'cancelled': return 'Отменено';
      case 'cancelling': return 'Отменяется';
      default: return status;
    }
  };

  // Форматирование времени
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('ru-RU');
  };

  useEffect(() => {
    if (activeTab === 'history' && historyRef.current.length === 0) {
      loadHistoryRef.current?.(1, historyPaginationRef.current.pageSize);
    }
  }, [activeTab]);

  if (loading && processes.length === 0) {
    return (
      <Card title="Мониторинг процессов" size="small">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>Загрузка процессов...</div>
        </div>
      </Card>
    );
  }

  const running = processes.filter(p => p.status === 'running' || p.status === 'cancelling');
  const pending = processes.filter(p => p.status === 'pending');
  const paused = processes.filter(p => p.status === 'paused');
  const slotUsage = resolveProcessSlotUsage(usage, queueLimits);
  const forceStopConfirm = resolveProcessMonitorConfirm('force-stop');

  const openProcessActionConfirm = (key, run, extra) => {
    const cfg = resolveProcessMonitorConfirm(key, extra);
    if (!cfg) {
      run();
      return;
    }
    Modal.confirm({
      title: cfg.title,
      content: cfg.content,
      okText: cfg.okText,
      cancelText: cfg.cancelText,
      okButtonProps: { danger: true },
      zIndex: 1100,
      onOk: run
    });
  };

  const confirmForceStopAll = () => openProcessActionConfirm('force-stop', forceStopAll);

  const bulkProcessActions = [
    {
      key: 'pause-all',
      label: 'Пауза всех',
      icon: <Pause size={14} aria-hidden />,
      onClick: pauseAll
    },
    {
      key: 'resume-all',
      label: 'Продолжить все',
      icon: <PlayCircle size={14} aria-hidden />,
      onClick: resumeAll
    },
    {
      key: 'stop-all',
      label: 'Остановить все',
      danger: true,
      icon: <StopCircle size={14} aria-hidden />,
      onClick: stopAll
    }
  ];

  const processActionPopupContainer = () =>
    (typeof document !== 'undefined' ? document.body : undefined);

  const renderProcessCardActions = (process, { forceStartDisabled = false } = {}) => {
    const defs = buildProcessCardActionKeys(process.status).map((key) => {
      if (key === 'pause') {
        return {
          key,
          label: 'Пауза',
          icon: <PauseCircle size={14} aria-hidden />,
          onClick: () => pauseProcess(process.id)
        };
      }
      if (key === 'resume') {
        return {
          key,
          label: 'Продолжить',
          type: 'primary',
          icon: <PlayCircle size={14} aria-hidden />,
          onClick: () => resumeProcess(process.id)
        };
      }
      if (key === 'force-start') {
        return {
          key,
          label: 'Принудительно запустить',
          type: 'primary',
          icon: <Zap size={14} aria-hidden />,
          disabled: forceStartDisabled,
          onClick: () => {
            if (!forceStartDisabled) forceStartProcess(process.id);
          }
        };
      }
      if (key === 'stop') {
        return {
          key,
          label: process.status === 'pending' ? 'Удалить из очереди' : 'Остановить',
          type: 'primary',
          danger: true,
          icon: <StopCircle size={14} aria-hidden />,
          onClick: () => stopProcess(process.id)
        };
      }
      return null;
    }).filter(Boolean);

    if (!defs.length) return null;

    if (isMobile) {
      return (
        <Dropdown
          menu={{
            items: defs.map(({ key, label, icon, danger, disabled, onClick }) => ({
              key,
              label,
              icon,
              danger,
              disabled,
              onClick: ({ domEvent }) => {
                domEvent?.preventDefault?.();
                domEvent?.stopPropagation?.();
                if (disabled) return;
                if (processMonitorActionNeedsConfirm(key)) {
                  openProcessActionConfirm(key, onClick, { status: process.status });
                  return;
                }
                onClick?.();
              }
            }))
          }}
          trigger={['click']}
          getPopupContainer={processActionPopupContainer}
        >
          <Button size="small" aria-label="Действия процесса">Действия</Button>
        </Dropdown>
      );
    }

    return (
      <Space wrap>
        {defs.map((item) => {
          const btn = (
            <Button
              key={item.key}
              size="small"
              type={item.type || 'default'}
              danger={item.danger}
              icon={item.icon}
              disabled={item.disabled}
              onClick={item.onClick}
            >
              {item.label}
            </Button>
          );
          if (item.key !== 'force-start') return btn;
          return (
            <Tooltip
              key={item.key}
              title={
                forceStartDisabled
                  ? 'Нельзя принудительно запустить: есть активные процессы'
                  : 'Принудительно переочередить процесс для запуска'
              }
            >
              <span>
                <Button
                  size="small"
                  type={item.type || 'default'}
                  danger={item.danger}
                  icon={item.icon}
                  disabled={item.disabled}
                  onClick={item.onClick}
                >
                  {item.label}
                </Button>
              </span>
            </Tooltip>
          );
        })}
      </Space>
    );
  };

  return (
    <Card
      title={embedded ? null : (
        <span style={{ display: 'block', lineHeight: 1.35 }}>
          Мониторинг процессов
        </span>
      )}
      extra={
        <Space wrap size={[8, 8]} style={{ maxWidth: '100%', justifyContent: 'flex-end' }}>
          <Button
            icon={<RotateCw size={14} aria-hidden />}
            size="small"
            onClick={() => {
              loadActiveProcesses();
              if (activeTab === 'history') {
                loadHistory(historyPagination.current, historyPagination.pageSize);
              }
            }}
            loading={loading || historyLoading}
          >
            Обновить
          </Button>
          {activeTab === 'processes' && (
            isMobile ? (
              <Dropdown
                menu={{
                  items: [
                    ...bulkProcessActions.map(({ key, label, icon, danger, onClick }) => ({
                      key,
                      label,
                      icon,
                      danger,
                      onClick: ({ domEvent }) => {
                        domEvent?.preventDefault?.();
                        domEvent?.stopPropagation?.();
                        if (processMonitorActionNeedsConfirm(key)) {
                          openProcessActionConfirm(key, onClick);
                          return;
                        }
                        onClick?.();
                      }
                    })),
                    { type: 'divider' },
                    {
                      key: 'force-stop',
                      label: 'Принудительная остановка',
                      danger: true,
                      icon: <StopCircle size={14} aria-hidden />,
                      onClick: ({ domEvent }) => {
                        domEvent?.preventDefault?.();
                        domEvent?.stopPropagation?.();
                        confirmForceStopAll();
                      }
                    }
                  ]
                }}
                trigger={['click']}
                getPopupContainer={processActionPopupContainer}
              >
                <Button size="small">Действия</Button>
              </Dropdown>
            ) : (
              <>
                <Button size="small" icon={<Pause size={14} aria-hidden />} onClick={pauseAll}>
                  Пауза всех
                </Button>
                <Button size="small" icon={<PlayCircle size={14} aria-hidden />} onClick={resumeAll}>
                  Продолжить все
                </Button>
                <Button danger size="small" onClick={stopAll}>
                  Остановить все
                </Button>
                <Popconfirm
                  title={forceStopConfirm.title}
                  description={forceStopConfirm.content}
                  onConfirm={forceStopAll}
                  okText={forceStopConfirm.okText}
                  cancelText={forceStopConfirm.cancelText}
                >
                  <Button danger size="small" icon={<StopCircle size={14} aria-hidden />}>
                    Принудительная остановка
                  </Button>
                </Popconfirm>
              </>
            )
          )}
        </Space>
      }
      size="small"
      styles={{
        header: { flexWrap: 'wrap', gap: 8, rowGap: 8 },
        body: { padding: isMobile ? 12 : 16 }
      }}
    >
      {error && (
        <Alert
          message="Ошибка"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* 🚀 УЛУЧШЕННАЯ Статистика использования */}
      {usage && activeTab === 'processes' && (
        <div style={{ 
          marginBottom: '16px', 
          padding: '12px', 
          background: token.colorFillTertiary, 
          borderRadius: token.borderRadius,
          border: `1px solid ${token.colorBorderSecondary}`
        }}>
          <Row gutter={[16, 12]}>
            {/* Основная информация о процессах */}
            <Col span={24}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
                <Text strong>
                  {slotUsage.title}
                  {slotUsage.tariffNote ? ` · ${slotUsage.tariffNote}` : ''}
                </Text>
                {lastUpdate && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <Timer size={12} style={{ marginRight: 4 }} aria-hidden />
                    Обновлено: {lastUpdate.toLocaleTimeString('ru-RU')}
                  </Text>
                )}
              </div>
              {slotUsage.hint && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 8 }}
                  message="Счётчик слотов"
                  description={slotUsage.hint}
                />
              )}
              {slotUsage.showProgress && (
                <Progress
                  percent={usage.processUsagePercent ?? 0}
                  size="small"
                  status={(usage.processUsagePercent ?? 0) > 90 ? 'exception' : (usage.processUsagePercent ?? 0) > 70 ? 'active' : 'normal'}
                  strokeColor={{
                    '0%': token.colorSuccess,
                    '70%': token.colorWarning,
                    '100%': token.colorError,
                  }}
                />
              )}
            </Col>
            
            {/* Статусы процессов */}
            <Col span={24}>
              <Space size="middle" wrap>
                <Tooltip title="Активно выполняющиеся процессы">
                  <Space>
                    <Badge count={running.length} style={{ backgroundColor: token.colorPrimary }} showZero />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <RefreshCw size={14} className={running.length > 0 ? 'mb-animate-spin' : undefined} aria-hidden /> Выполняется
                    </Text>
                  </Space>
                </Tooltip>
                <Tooltip title="Процессы в очереди ожидания">
                  <Space>
                    <Badge count={pending.length} style={{ backgroundColor: token.colorWarning }} showZero />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <Clock size={14} aria-hidden /> Ожидает
                    </Text>
                  </Space>
                </Tooltip>
                <Tooltip title="Процессы на паузе">
                  <Space>
                    <Badge count={paused.length} style={{ backgroundColor: '#722ed1' }} showZero />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      <PauseCircle size={14} aria-hidden /> На паузе
                    </Text>
                  </Space>
                </Tooltip>
              </Space>
            </Col>
            
            {/* Лимиты user_resource_limits; при backpressure не показывать как «исчерпан лимит» */}
            {slotUsage.showRemaining && (
              <Col span={24}>
                <div style={{ 
                  marginTop: 4, 
                  padding: '8px 12px', 
                  background: usage.remainingProcesses === 0 ? token.colorErrorBg : token.colorInfoBg,
                  borderRadius: token.borderRadius,
                  fontSize: 12
                }}>
                  {usage.maxProcesses === 0 ? (
                    <Text type="danger">
                      <AlertTriangle size={14} aria-hidden /> Лимит параллельных процессов в профиле равен 0. Обратитесь к администратору.
                    </Text>
                  ) : usage.remainingProcesses === 0 ? (
                    <Text type="danger">
                      <AlertTriangle size={14} aria-hidden /> Достигнут лимит процессов. Дождитесь завершения текущих.
                    </Text>
                  ) : (
                    <Text type="secondary">
                      Доступно слотов: <Text strong>{usage.remainingProcesses}</Text> из {usage.maxProcesses}
                    </Text>
                  )}
                </div>
              </Col>
            )}
            
            {/* 🆕 Детальная информация о тарифных лимитах */}
            {queueLimits && (
              <Col span={24}>
                <div style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  background: token.colorFillQuaternary,
                  borderRadius: token.borderRadius,
                  fontSize: 11
                }}>
                  <Space size="middle" wrap>
                    {queueLimits.max_concurrent_processes && (
                      <Tooltip title="Максимальное количество одновременных процессов по вашему тарифу">
                        <Space size={4}>
                          <Text type="secondary">Макс. процессов:</Text>
                          <Text strong>{queueLimits.max_concurrent_processes}</Text>
                        </Space>
                      </Tooltip>
                    )}
                    {queueLimits.bandwidth_percent && (
                      <Tooltip title="Процент пропускной способности сервера, выделенный вашему тарифу">
                        <Space size={4}>
                          <Text type="secondary">Приоритет:</Text>
                          <Text strong>{queueLimits.bandwidth_percent}%</Text>
                        </Space>
                      </Tooltip>
                    )}
                    {queueLimits.max_batch_size && (
                      <Tooltip title="Максимальный размер пакета для обработки данных">
                        <Space size={4}>
                          <Text type="secondary">Размер пакета:</Text>
                          <Text strong>{queueLimits.max_batch_size.toLocaleString('ru-RU')}</Text>
                        </Space>
                      </Tooltip>
                    )}
                    {queueLimits.tariff_name && (
                      <Tag color="blue" size="small">
                        Тариф: {queueLimits.tariff_name}
                      </Tag>
                    )}
                  </Space>
                </div>
              </Col>
            )}
          </Row>
        </div>
      )}

      {/* Вкладки: Процессы и История */}
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        {
          key: 'processes',
          label: <Badge count={processes.length} offset={[10, 0]}><span>Процессы</span></Badge>,
          children: (
            <>
              {/* Список процессов: сперва выполняющиеся, затем ожидающие */}
              {processes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: token.colorTextSecondary }}>
                  <Clock size={24} style={{ marginBottom: '8px' }} aria-hidden />
                  <div>Нет активных процессов</div>
                </div>
              ) : (
                <div>
                  {/* Выполняющиеся процессы */}
                  {running.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ marginBottom: 12, fontWeight: 'bold', fontSize: '16px', color: token.colorPrimary }}>
                        Выполняется ({running.length})
                      </div>
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {running.map((process) => {
                          const counts = resolveProcessCounts(process);
                          const stuck = isProcessVisiblyStuck(process);
                          const heartbeatAt = resolveProcessHeartbeatAt(process);
                          const kindTag = resolveProcessKindTag(process);
                          const progressMeta = {
                            ...(process.metadata || {}),
                            itemsProcessed: counts.done,
                            downloaded: counts.done,
                            totalItems: counts.total,
                            estimatedMinutesLeft: resolveProcessEtaMinutes(process),
                            retry_count: process.retry_count ?? process.metadata?.retry_count,
                            retryCount: process.retry_count ?? process.metadata?.retryCount
                          };
                          return (
                          <Card
                            key={process.id}
                            size="small"
                            style={{
                              borderLeft: `3px solid ${token.colorPrimary}`,
                              borderRadius: token.borderRadius
                            }}
                          >
                            <div style={{ width: '100%' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
                                <Space wrap>
                                  <Tag color={getStatusColor(process.status)} icon={getStatusIcon(process.status)}>
                                    {getStatusText(process.status)}
                                  </Tag>
                                  <Text strong style={{ fontSize: '15px' }}>{stripUserVisibleUuid(process.process_name) || 'Процесс'}</Text>
                                  {process.queue_position && (
                                    <Tag color={token.colorPrimary}>Позиция: {process.queue_position}</Tag>
                                  )}
                                  {process.priority && (
                                    <Tag color="purple">Приоритет: {process.priority}</Tag>
                                  )}
                                </Space>
                              </div>

                              {(process.status === 'running' || counts.total > 0 || typeof process.progress_percent === 'number') && (
                                <div style={{ marginBottom: 8 }}>
                                  <EnhancedProgressBar
                                    percent={counts.percent}
                                    status={process.status}
                                    stage={
                                      process.metadata?.stage && process.metadata.stage !== process.process_name
                                        ? process.metadata.stage
                                        : undefined
                                    }
                                    metadata={progressMeta}
                                    size="small"
                                    showDetails
                                    isStuck={stuck}
                                    processType={process.metadata?.dataType || process.process_type}
                                  />
                                </div>
                              )}

                              <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '6px',
                                marginBottom: 12,
                                paddingBottom: 12,
                                borderBottom: `1px solid ${token.colorBorderSecondary}`
                              }}>
                                {(process.supplierName || process.metadata?.supplier) && (
                                  <Tag size="small" color="blue" icon={<Webhook size={12} aria-hidden />}>
                                    {process.supplierName || process.metadata.supplier}
                                  </Tag>
                                )}
                                {kindTag.show && (
                                  <Tag size="small" color="cyan">
                                    {kindTag.text}
                                  </Tag>
                                )}
                                {process.warehouseId && (
                                  <Tooltip title={formatWarehouseLabel({ name: warehouseLabelById?.[String(process.warehouseId)] }, { supplierName: process.supplierName || process.metadata?.supplier, includeSupplier: true })}>
                                    <Tag size="small" color="purple">
                                      {(() => {
                                        const wid = String(process.warehouseId);
                                        const supplierName = process.supplierName || process.metadata?.supplier || null;
                                        const name = warehouseLabelById?.[wid] || null;
                                        return formatWarehouseLabel({ name }, { supplierName, includeSupplier: true });
                                      })()}
                                    </Tag>
                                  </Tooltip>
                                )}
                                {process.resourceUsage?.peakMemory && (
                                  <Tag size="small" color="orange">
                                    Память: {Math.round(process.resourceUsage.peakMemory / 1024 / 1024)}MB
                                  </Tag>
                                )}
                              </div>

                              {/* Футер с кнопками и временем */}
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 8
                              }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', fontSize: '12px' }}>
                                  <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Создан: {formatTime(process.created_at)}
                                  </Text>
                                  {process.started_at && (
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                      Запущен: {formatTime(process.started_at)}
                                    </Text>
                                  )}
                                  {heartbeatAt && process.status === 'running' && (
                                    <Text
                                      type="secondary"
                                      style={{
                                        fontSize: '12px',
                                        color: stuck ? token.colorError : token.colorTextSecondary
                                      }}
                                    >
                                      Обновлено: {formatTime(heartbeatAt)}
                                    </Text>
                                  )}
                                  {process.completed_at && (
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                      Завершен: {formatTime(process.completed_at)}
                                    </Text>
                                  )}
                                </div>

                                {renderProcessCardActions(process)}
                              </div>

                              {process.error_message && (
                                <Alert
                                  message="Ошибка выполнения"
                                  description={sanitizeUserVisibleOpsText(process.error_message) || 'Процесс завершился с ошибкой'}
                                  type="error"
                                  showIcon
                                  size="small"
                                  style={{ marginTop: '8px' }}
                                />
                              )}
                            </div>
                          </Card>
                          );
                        })}
                      </Space>
                    </div>
                  )}

                  {/* Ожидающие процессы */}
                  {pending.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ marginBottom: 12, fontWeight: 'bold', fontSize: '16px', color: token.colorWarning }}>
                        В очереди ({pending.length})
                      </div>
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {pending.map((process) => {
                          const kindTag = resolveProcessKindTag(process);
                          return (
                          <Card
                            key={process.id}
                            size="small"
                            style={{
                              borderLeft: `3px solid ${token.colorWarning}`,
                              borderRadius: token.borderRadius
                            }}
                          >
                            <div style={{ width: '100%' }}>
                              {/* Заголовок процесса */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                                <Space wrap>
                                  <Tag color={getStatusColor(process.status)} icon={getStatusIcon(process.status)}>
                                    {getStatusText(process.status)}
                                  </Tag>
                                  <Text strong style={{ fontSize: '15px' }}>{stripUserVisibleUuid(process.process_name) || 'Процесс'}</Text>
                                  {process.queue_position && (
                                    <Tag color={token.colorPrimary}>Позиция: {process.queue_position}</Tag>
                                  )}
                                </Space>
                              </div>

                              {/* Метаданные процесса */}
                              {process.metadata && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: 12 }}>
                                  {process.metadata.supplier && (
                                    <Tag size="small" color="blue">
                                      {process.metadata.supplier}
                                    </Tag>
                                  )}
                                  {kindTag.show && (
                                    <Tag size="small" color="cyan">
                                      {kindTag.text}
                                    </Tag>
                                  )}
                                </div>
                              )}

                              {/* Управление приоритетом и информацией */}
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                padding: '8px 0',
                                borderTop: `1px solid ${token.colorBorderSecondary}`,
                                borderBottom: `1px solid ${token.colorBorderSecondary}`
                              }}>
                                <Space>
                                  <Text type="secondary" style={{ fontSize: '13px' }}>Приоритет:</Text>
                                  <Space.Compact>
                                    <Tooltip title="Уменьшить приоритет">
                                      <Button
                                        size="small"
                                        icon={<ChevronDown size={14} aria-hidden />}
                                        onClick={() => {
                                          const newPriority = Math.max(1, (process.priority || 5) - 1);
                                          updatePriority(process.id, newPriority);
                                        }}
                                      />
                                    </Tooltip>
                                    <InputNumber
                                      min={1}
                                      max={10}
                                      value={process.priority || 5}
                                      size="small"
                                      style={{ width: 70, textAlign: 'center' }}
                                      onChange={(value) => {
                                        if (value && value >= 1 && value <= 10) {
                                          updatePriority(process.id, value);
                                        }
                                      }}
                                    />
                                    <Tooltip title="Увеличить приоритет">
                                      <Button
                                        size="small"
                                        icon={<ChevronUp size={14} aria-hidden />}
                                        onClick={() => {
                                          const newPriority = Math.min(10, (process.priority || 5) + 1);
                                          updatePriority(process.id, newPriority);
                                        }}
                                      />
                                    </Tooltip>
                                  </Space.Compact>
                                </Space>
                                {(() => {
                                  const etaLabel = formatEtaMinutes(resolveProcessEtaMinutes(process));
                                  if (!etaLabel) return null;
                                  return (
                                  <Text type="secondary" style={{ fontSize: '13px' }}>
                                    Ожидаемое время: {etaLabel}
                                  </Text>
                                  );
                                })()}
                              </div>

                              {/* Футер с временем и кнопкой */}
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                marginTop: 12,
                                flexWrap: 'wrap',
                                gap: 8
                              }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  Создан: {formatTime(process.created_at)}
                                </Text>
                                {renderProcessCardActions(process, {
                                  forceStartDisabled: running.length > 0 || paused.length > 0
                                })}
                              </div>
                            </div>
                          </Card>
                          );
                        })}
                      </Space>
                    </div>
                  )}

                  {/* Пауза */}
                  {paused.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ marginBottom: 12, fontWeight: 'bold', fontSize: '16px', color: token.colorWarning }}>
                        На паузе ({paused.length})
                      </div>
                      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        {paused.map((process) => {
                          const kindTag = resolveProcessKindTag(process);
                          return (
                          <Card
                            key={process.id}
                            size="small"
                            style={{
                              borderLeft: `3px solid ${token.colorWarning}`,
                              borderRadius: token.borderRadius
                            }}
                          >
                            <div style={{ width: '100%' }}>
                              {/* Заголовок процесса */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                                <Space wrap>
                                  <Tag color={getStatusColor(process.status)} icon={getStatusIcon(process.status)}>
                                    {getStatusText(process.status)}
                                  </Tag>
                                  <Text strong style={{ fontSize: '15px' }}>{stripUserVisibleUuid(process.process_name) || 'Процесс'}</Text>
                                </Space>
                                {renderProcessCardActions(process)}
                              </div>

                              {/* Метаданные процесса */}
                              {process.metadata && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: 12 }}>
                                  {process.metadata.supplier && (
                                    <Tag size="small" color="blue">
                                      {process.metadata.supplier}
                                    </Tag>
                                  )}
                                  {kindTag.show && (
                                    <Tag size="small" color="cyan">
                                      {kindTag.text}
                                    </Tag>
                                  )}
                                </div>
                              )}

                              {/* Футер с временем */}
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                paddingTop: 8,
                                borderTop: `1px solid ${token.colorBorderSecondary}`
                              }}>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  Создан: {formatTime(process.created_at)}
                                </Text>
                                {process.updated_at && (
                                  <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Обновлен: {formatTime(process.updated_at)}
                                  </Text>
                                )}
                              </div>
                            </div>
                          </Card>
                          );
                        })}
                      </Space>
                    </div>
                  )}

                  {/* Показывать только если нет активных процессов */}
                  {running.length === 0 && pending.length === 0 && paused.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '20px', color: token.colorTextSecondary }}>
                      <Clock size={24} style={{ marginBottom: '8px' }} aria-hidden />
                      <div>Нет активных процессов</div>
                    </div>
                  )}
                </div>
              )}
            </>
          )
        },
        {
          key: 'history',
          label: <Badge count={historyPagination.total} offset={[10, 0]}><span>История</span></Badge>,
          children: (
            <Table
            dataSource={history}
            loading={historyLoading}
            rowKey="id"
            size="small"
            scroll={{ x: 800 }}
            pagination={{
              current: historyPagination.current,
              pageSize: historyPagination.pageSize,
              total: historyPagination.total,
              onChange: (page, pageSize) => {
                loadHistory(page, pageSize);
              },
              showSizeChanger: true,
              showTotal: (total) => `Всего: ${total}`
            }}
            columns={[
              {
                title: 'Операция',
                key: 'operation',
                width: 180,
                render: (_, record) => {
                  const kindTag = resolveProcessKindTag(record);
                  return (
                    <Space direction="vertical" size={2}>
                      <Text strong style={{ fontSize: 13 }}>{stripUserVisibleUuid(record.operation_name || record.operation_type || record.process_name) || 'Операция'}</Text>
                      <Space size={4} wrap>
                        {record.supplier_name && (
                          <Tag size="small" color="blue" icon={<Webhook size={12} aria-hidden />}>{record.supplier_name}</Tag>
                        )}
                        {kindTag.show && (
                          <Tag size="small" color="cyan">{kindTag.text}</Tag>
                        )}
                      </Space>
                    </Space>
                  );
                }
              },
              {
                title: 'Статус',
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (status) => (
                  <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
                    {getStatusText(status)}
                  </Tag>
                )
              },
              {
                title: 'Прогресс',
                key: 'progress',
                width: 150,
                render: (_, record) => {
                  const counts = resolveProcessCounts(record);
                  const progressMeta = {
                    ...(record.metadata || {}),
                    itemsProcessed: counts.done,
                    downloaded: counts.done,
                    totalItems: counts.total
                  };
                  return (
                  <EnhancedProgressBar
                    percent={counts.percent}
                    status={record.status}
                    stage={record.metadata?.stage}
                    metadata={progressMeta}
                    size="small"
                    showDetails={false}
                    isStuck={false}
                    processType={record.process_type}
                  />
                  );
                }
              },
              {
                title: 'Статистика',
                key: 'stats',
                width: 140,
                render: (_, record) => {
                  const counts = resolveProcessCounts(record);
                  const retry = resolveProcessRetry(record);
                  
                  return (
                    <Space direction="vertical" size={2}>
                      {(counts.total > 0 || counts.done > 0) && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {counts.done.toLocaleString('ru-RU')}/
                          {counts.total.toLocaleString('ru-RU')}
                        </Text>
                      )}
                      {/* Успешные/неудачные */}
                      <Space size={4}>
                        {record.items_success > 0 && (
                          <Tag color="green" size="small">+{record.items_success.toLocaleString('ru-RU')}</Tag>
                        )}
                        {record.items_failed > 0 && (
                          <Tag color="red" size="small">-{record.items_failed.toLocaleString('ru-RU')}</Tag>
                        )}
                      </Space>
                      {retry.show && (
                        <Tooltip title={`Было ${retry.count} повтор(ов) из ${retry.max}`}>
                          <Tag size="small" color="orange" icon={<Repeat size={12} aria-hidden />}>
                            {retry.label}
                          </Tag>
                        </Tooltip>
                      )}
                    </Space>
                  );
                }
              },
              {
                title: 'Длительность',
                key: 'duration',
                width: 90,
                render: (_, record) => {
                  const duration = record.duration_seconds || 
                                   record.actual_duration_minutes * 60 ||
                                   (record.metadata?.elapsedMinutes * 60);
                  if (duration) {
                    if (duration < 60) {
                      return <Text type="secondary">{Math.round(duration)}с</Text>;
                    }
                    const minutes = Math.floor(duration / 60);
                    const seconds = Math.round(duration % 60);
                    if (minutes >= 60) {
                      const hours = Math.floor(minutes / 60);
                      const mins = minutes % 60;
                      return <Text type="secondary">{hours}ч {mins > 0 ? `${mins}м` : ''}</Text>;
                    }
                    return <Text type="secondary">{minutes}м {seconds > 0 ? `${seconds}с` : ''}</Text>;
                  }
                  return <Text type="secondary">-</Text>;
                }
              },
              {
                title: 'Дата',
                key: 'date',
                width: 140,
                render: (_, record) => (
                  <Space direction="vertical" size="small">
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {formatTime(record.started_at || record.created_at)}
                    </Text>
                    {record.completed_at && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Завершено: {formatTime(record.completed_at)}
                      </Text>
                    )}
                  </Space>
                )
              },
              {
                title: 'Ошибка',
                key: 'error',
                render: (_, record) => {
                  if (record.error_message) {
                    return (
                      <Tooltip title={sanitizeUserVisibleOpsText(record.error_message) || 'Ошибка'}>
                        <Tag color="red" size="small">Ошибка</Tag>
                      </Tooltip>
                    );
                  }
                  return '-';
                }
              }
            ]}
          />
          )
        }
      ]} />
    </Card>
  );
};

export default ProcessProgressMonitor;
